import { useEffect, useMemo } from 'react';

import type { ApiListingApplicationId } from '#/api/types/ApiTypedId';
import { JobDetailsList } from '#/components/DetailsList/Job/JobDetailsList';
import { PageToolbar } from '#/components/PageToolbar/PageToolbar';
import { PrintHidden } from '#/components/PrintHidden/PrintHidden';
import { useUser } from '#/contexts/UserEnvironmentContext';
import { useIsExternalUrl } from '#/hooks/useIsExternalUrl';
import { useI18n } from '#/i18n/hooks/useI18n';
import { trackViewListing } from '#/modules/listing/analytics/trackViewListing';
import { ListingViewAdminTools } from '#/modules/listing/components/ListingView/AdminTools/ListingViewAdminTools';
import { ListingViewAppliedMessage } from '#/modules/listing/components/ListingView/AppliedMessage/ListingViewAppliedMessage';
import { ListingViewApplyAts } from '#/modules/listing/components/ListingView/Apply/ListingViewApplyAts';
import { ListingViewApplyExternal } from '#/modules/listing/components/ListingView/Apply/ListingViewApplyExternal';
import { ListingViewIdealistAtsUserApplicationForm } from '#/modules/listing/components/ListingView/AtsApplicationForm/ListingViewIdealistAtsUserApplicationForm';
import { ListingViewBody } from '#/modules/listing/components/ListingView/Body/ListingViewBody';
import { ListingViewFooter } from '#/modules/listing/components/ListingView/Footer/ListingViewFooter';
import { ListingViewHeader } from '#/modules/listing/components/ListingView/Header/ListingViewHeader';
import { ListingViewHeaderSearchContext } from '#/modules/listing/components/ListingView/HeaderSearchContext/ListingViewHeaderSearchContext';
import { ListingViewMain } from '#/modules/listing/components/ListingView/Main/ListingViewMain';
import { ListingViewSection } from '#/modules/listing/components/ListingView/Section/ListingViewSection';
import { ListingViewSignupCta } from '#/modules/listing/components/ListingView/SignupCta/ListingViewSignupCta';
import { ListingViewDescriptionSection } from '#/modules/listing/components/ListingView/sections/Description/ListingViewDescriptionSection';
import { ListingViewApplySection } from '#/modules/listing/components/ListingView/sections/ListingViewApplySection';
import { ListingViewBenefitsSection } from '#/modules/listing/components/ListingView/sections/ListingViewBenefitsSection';
import { ListingViewLanguageProficiencySection } from '#/modules/listing/components/ListingView/sections/ListingViewLanguageProficiencySection';
import { ListingViewLocationSection } from '#/modules/listing/components/ListingView/sections/ListingViewLocationSection';
import { trackViewListingAtsSection } from '#/modules/listing/helpers/trackViewListingAtsSection';
import { useListingApplicationSubmission } from '#/modules/listing/hooks/listingApplicationSubmission/useListingApplicationSubmission';
import { useSaveListingAndOrg } from '#/modules/listing/hooks/useSaveListingAndOrg';
import type { ApiJob } from '#/modules/listing/jobs/api/types/ApiJob';
import { ViewJobActionButtons } from '#/modules/listing/jobs/components/ActionButtons/ViewJobActionButtons';
import { getJobApplySectionTitle } from '#/modules/listing/jobs/helpers/getJobApplySectionTitle';
import type { ListingTmpStorageListingApplicationFailure } from '#/modules/listing/types/ListingTmpStorageListingApplicationFailure';
import { ListingViewStatus } from '#/modules/listing/types/ListingViewStatus';
import { SearchContextHeader } from '#/modules/search/components/Context/SearchContextHeader';
import { useSearchContextHeader } from '#/modules/search/containers/SearchContextHeader/useSearchContextHeader';
import { useSearchSavedListingsStore } from '#/modules/search/zustand-stores/searchSavedListingsStore';
import { trackClicked } from '#/utils/analytics/track/trackClicked';
import { isUserOrgOwner } from '#/utils/listings/getOrgOwner';
import { showModal } from '#/zustand-stores/modalStore';

import { ViewJobPageRecommendations } from './components/Recommendations/ViewJobPageRecommendations';
import { ViewJobPageSidebar } from './components/Sidebar/ViewJobPageSidebar';
import { UnpublishedJobToast } from './components/UnpublishedJobToast/UnpublishedJobToast';
import { useJobApplicationStatus } from './useJobApplicationStatus';

type Props = {
  job: ApiJob;
  initialListingApplicationId: ApiListingApplicationId | null;
  initiallySaved: boolean;
  orgInitiallySaved: boolean;
  listingApplicationFailure: ListingTmpStorageListingApplicationFailure | null;
};

export function ViewJobPage({
  job,
  initialListingApplicationId,
  initiallySaved,
  orgInitiallySaved,
  listingApplicationFailure,
}: Props) {
  const { i18n, locale } = useI18n();
  const { isExternalUrl } = useIsExternalUrl();

  const { user } = useUser();
  const isOrgOwner = isUserOrgOwner(user, job.org?.id);

  const { searchContext } = useSearchContextHeader({
    currentObjectId: job.id,
  });
  const { searchSavedListingsStore } = useSearchSavedListingsStore();

  const {
    save,
    saved,
    unsave,
    orgSaved,
    saveOrg,
    unsaveOrg,
    shouldShowSaveOrgTooltip,
    dismissSaveOrgTooltip,
  } = useSaveListingAndOrg({
    listing: job,
    initiallySaved,
    orgInitiallySaved,
    searchSavedListingsStore,
  });

  const { listingApplicationId, markAsApplied, markAsNotApplied, onApplied } =
    useJobApplicationStatus({ job, initialListingApplicationId });
  const applied = Boolean(listingApplicationId);

  const listingDetailsSearchResultsEnabled = useMemo(() => {
    if (isOrgOwner && !searchContext) return false;

    return true;
  }, [isOrgOwner, searchContext]);

  useEffect(() => {
    trackViewListing({
      status: 'published',
      hasAts: job.hasAts,
      listingName: job.name,
      listingId: job.id,
      orgId: job.org?.id,
      orgName: job.org?.name,
      orgType: job.org?.orgType,
      listingType: 'JOB',
    });
  }, [job]);

  const orgId = job.org?.id ?? null;
  const orgName = job.orgNameOverride ?? job.org?.name ?? null;
  const orgUrl = job.org?.url[locale] ?? null;
  const orgType = job.org?.orgType ?? null;

  const {
    listingApplicationInitialValues,
    listingApplicationInitialErrors,
    submitListingApplication,
  } = useListingApplicationSubmission({
    listingId: job.id,
    listingType: job.type,
    orgId,
    orgType,
    applied,
    onApplied,
    listingApplicationFailure,
  });

  return (
    <>
      {!job.isPublished && <UnpublishedJobToast />}

      {isOrgOwner && job.org && (
        <PageToolbar
          centered
          message={i18n('Your organization posted this job')}
          tools={
            <ListingViewAdminTools
              listingId={job.id}
              listingType="JOB"
              orgId={job.org.id}
              hasAts={job.hasAts}
              canPost
            />
          }
        />
      )}

      <ListingViewBody>
        <ViewJobPageSidebar
          job={job}
          applied={applied}
          listingDetailsSearchResultsEnabled={
            listingDetailsSearchResultsEnabled
          }
          searchContext={searchContext}
          searchSavedListingsStore={searchSavedListingsStore}
          listingViewStatus={ListingViewStatus.PUBLISHED}
        />

        <ListingViewMain>
          {searchContext && (
            <ListingViewHeaderSearchContext searchContext={searchContext} />
          )}

          <ListingViewHeader
            address={job.address}
            isPostedAnonymously={job.isPostedAnonymously}
            listingId={job.id}
            listingName={job.name}
            listingType={job.type}
            listingFirstPublished={job.firstPublished}
            orgName={orgName}
            orgType={orgType}
            orgUrl={orgUrl}
            remoteOptions={job.remoteOptions}
            listingViewStatus={ListingViewStatus.PUBLISHED}
            actionButtons={
              <ViewJobActionButtons
                job={job}
                applied={applied}
                onMarkAsApplied={markAsApplied}
                onMarkAsNotApplied={markAsNotApplied}
                jobSaved={saved}
                onSaveJob={save}
                onUnsaveJob={unsave}
                orgSaved={orgSaved}
                onSaveOrg={saveOrg}
                onUnsaveOrg={unsaveOrg}
                shouldShowSaveOrgTooltip={shouldShowSaveOrgTooltip}
                onDismissSaveOrgTooltip={dismissSaveOrgTooltip}
              />
            }
          />

          <ListingViewSection title={i18n('Details')} noBorderTop>
            <JobDetailsList
              startDate={job.startDate}
              endDate={job.endDate}
              applicationDeadline={job.applicationDeadline}
              education={job.education}
              isContract={job.isContract}
              isFullTime={job.isFullTime}
              isTemporary={job.isTemporary}
              professionalLevel={job.professionalLevel}
              salaryCurrency={job.salaryCurrency}
              salaryDetails={job.salaryDetails}
              salaryMaximum={job.salaryMaximum}
              salaryMinimum={job.salaryMinimum}
              salaryPeriod={job.salaryPeriod}
              jobCountry={job.address.country}
              areasOfFocus={job.areasOfFocus}
            />
          </ListingViewSection>

          <ListingViewDescriptionSection
            listingName={job.name}
            image={null}
            description={job.description}
            descriptionTruncated={job.descriptionTruncated}
            handleShowEmailModal={(link) => {
              if (!user && isExternalUrl(link.href)) {
                showModal('EMAIL_ALERT', {
                  listingFunctions: job.functions,
                  orgId: job.org?.id,
                  listingName: job.name,
                  listingType: 'JOB',
                  synonym: job.synonym,
                });
              }
            }}
            showMoreLinkTitle={i18n('Read more about this job')}
            onToggleDetails={(isOpen) => {
              if (!isOpen) return;

              trackClicked('read more about this listing', {
                listing_type: 'JOB',
                locale: job.locale,
              });
            }}
          />

          <ListingViewBenefitsSection
            benefits={job.benefits}
            benefitsTruncated={job.benefitsTruncated}
            onToggleDetails={(isOpen) => {
              if (!isOpen) return;

              trackClicked('read more about benefits', {
                listing_type: 'JOB',
                locale: job.locale,
              });
            }}
          />

          <ListingViewLanguageProficiencySection
            languageProficiency={job.languageProficiency}
            languageProficiencyTruncated={job.languageProficiencyTruncated}
          />

          <ListingViewLocationSection
            listingType="JOB"
            address={job.address}
            remoteOptions={job.remoteOptions}
          />

          <ListingViewApplySection
            title={getJobApplySectionTitle({ i18n, hasAts: job.hasAts })}
          >
            {job.hasAts ? (
              // oxlint-disable-next-line react/jsx-no-useless-fragment
              <>
                {applied ? (
                  <ListingViewAppliedMessage />
                ) : (
                  <ListingViewApplyAts
                    applyText={job.applyText}
                    applicationForm={
                      <ListingViewIdealistAtsUserApplicationForm
                        listingId={job.id}
                        listingType="JOB"
                        requestedAttachments={job.requestedAttachments}
                        submission={{
                          listingApplicationInitialValues,
                          listingApplicationInitialErrors,
                          onSubmit: submitListingApplication,
                          requestedAttachmentsHash:
                            job.requestedAttachmentsHash,
                        }}
                      />
                    }
                    onReach={() =>
                      trackViewListingAtsSection({
                        listingId: job.id,
                        listingType: 'JOB',
                        orgId,
                        orgType,
                        applied,
                      })
                    }
                  />
                )}
              </>
            ) : (
              <ListingViewApplyExternal
                applyEmail={job.applyEmail}
                applyText={job.applyText}
                applyTextTruncated={job.applyTextTruncated}
                applyUrl={job.applyUrl}
                functions={job.functions}
                listingName={job.name}
                listingType="JOB"
                listingId={job.id}
                orgId={orgId}
                orgType={orgType}
                synonym={job.synonym}
                applied={applied}
                onMarkAsApplied={markAsApplied}
                isPreview={false}
              />
            )}
          </ListingViewApplySection>

          {searchContext ? (
            <PrintHidden>
              <div style={{ marginTop: 70 }}>
                <SearchContextHeader searchContext={searchContext} noDivider />
              </div>
            </PrintHidden>
          ) : null}
        </ListingViewMain>
      </ListingViewBody>

      <ListingViewFooter
        hiddenOnDesktop={Boolean(user && listingDetailsSearchResultsEnabled)}
      >
        <ViewJobPageRecommendations
          jobId={job.id}
          jobTitle={job.name}
          synonym={job.synonym}
          functions={job.functions}
          orgAreasOfFocus={job.org?.areasOfFocusKeys}
        />

        {!user && (
          <ListingViewSignupCta listingUrl={job.url[locale]} type="JOB" />
        )}
      </ListingViewFooter>
    </>
  );
}
